import { Roles } from '@common/types/user';
import { CompanySetupContext } from '@company/contexts/CompanySetupContext';
import { Select } from '@components/utility/selects';
import { getMarketIcons } from '@event/utils';
import { useAppSelector } from '@hooks/generic';
import { Box, Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete/Autocomplete';
import TextField from '@mui/material/TextField/TextField';
import { useGetEndUsersQuery } from '@services/accounts';
import { useGetMarketsQuery } from '@services/markets';
import { useGetServicesQuery } from '@services/services';
import { selectUser } from '@store/userSlice';
import { useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const SetupStep = () => {
    const [endUsersSearch, setEndUsersSearch] = useState('');
    const user = useAppSelector(selectUser);
    const dashboard = useMemo(() => window.location.pathname.toLowerCase().includes('/dashboard/'), []);
    const intl = useIntl();

    const marketIcons = getMarketIcons(intl);

    const {
        formik: { values, handleChange, touched, handleBlur, errors, setFieldValue },
    } = useContext(CompanySetupContext);

    const { data: markets, isLoading: isMarketsLoading } = useGetMarketsQuery(undefined, {
        selectFromResult: (result) => ({
            ...result,
            data: result.data?.map((market) => ({
                label: market.title,
                value: market.id,
                icon: marketIcons[market.slug].icon,
            })),
        }),
    });
    const { data: endUsers, isLoading: isEndUsersLoading } = useGetEndUsersQuery(
        { search: endUsersSearch },
        {
            selectFromResult: (result) => ({
                ...result,
                data: result.data?.map((market) => ({
                    label: market.full_name,
                    value: market.id,
                })),
            }),
            skip: endUsersSearch.length < 3,
        }
    );
    const { data: services, isLoading: isServicesLoading } = useGetServicesQuery(undefined, {
        selectFromResult: (result) => ({
            ...result,
            data: result.data?.map((service) => ({
                label: service.name,
                value: service.id,
            })),
        }),
    });

    return (
        <>
            <TextField
                name="name"
                fullWidth
                label="Company name"
                value={values.name ?? ''}
                required
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                onBlur={handleBlur}
            />
            {dashboard && user?.role === Roles.VICTRON ? (
                <Select
                    searchable
                    searchProps={{
                        onChange: (e) => setEndUsersSearch(e.target.value),
                        placeholder: 'Type at least 3 characters',
                    }}
                    label="Owner"
                    name="owner_id"
                    items={endUsers ?? []}
                    value={values.owner_id ?? ''}
                    onChange={handleChange}
                    virtualize
                    type="default"
                    loading={isEndUsersLoading}
                    error={touched.owner_id && Boolean(errors.owner_id)}
                    helperText={touched.owner_id && errors.owner_id}
                    required
                    fcSX={{ required: true }}
                />
            ) : null}
            <TextField
                name="vat_number"
                fullWidth
                label="VAT number"
                value={values.vat_number ?? ''}
                onChange={handleChange}
                error={touched.vat_number && Boolean(errors.vat_number)}
                helperText={touched.vat_number && errors.vat_number}
                onBlur={handleBlur}
            />
            <TextField
                name="chamber_of_commerce_number"
                fullWidth
                required
                label="Chamber of commerce number"
                value={values.chamber_of_commerce_number ?? ''}
                onChange={handleChange}
                error={touched.chamber_of_commerce_number && Boolean(errors.chamber_of_commerce_number)}
                helperText={touched.chamber_of_commerce_number && errors.chamber_of_commerce_number}
                onBlur={handleBlur}
            />
            <Autocomplete
                multiple
                renderInput={(params) => (
                    <TextField
                        {...params}
                        name="services"
                        label="Services"
                        slotProps={{
                            inputLabel: {
                                required: true,
                            },
                        }}
                        error={touched.services && Boolean(errors.services)}
                        helperText={touched.services && errors.services}
                    />
                )}
                options={services ?? []}
                onChange={(_e, value) =>
                    setFieldValue(
                        'services',
                        value.map((x) => ({ id: x.value }))
                    )
                }
                value={
                    services?.filter((service) => values.services?.map((value) => value.id).includes(service.value)) ??
                    []
                }
                onBlur={handleBlur}
                fullWidth
                loading={isServicesLoading}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.label === value.label}
                filterSelectedOptions
            />
            <Autocomplete
                multiple
                renderInput={(params) => (
                    <TextField
                        {...params}
                        name="markets_ids"
                        label="Markets"
                        slotProps={{
                            inputLabel: {
                                required: true,
                            },
                        }}
                        error={touched.markets_ids && Boolean(errors.markets_ids)}
                        helperText={touched.markets_ids && errors.markets_ids}
                    />
                )}
                options={markets ?? []}
                onChange={(_e, value) =>
                    setFieldValue(
                        'markets_ids',
                        value.map((x) => x.value)
                    )
                }
                value={markets?.filter((market) => values.markets_ids?.includes(market.value)) ?? []}
                onBlur={handleBlur}
                fullWidth
                loading={isMarketsLoading}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.label === value.label}
                filterSelectedOptions
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                        <Box key={key} component="li" {...optionProps}>
                            <option.icon width={16} height={16} className="mr-2" />
                            {option.label}
                        </Box>
                    );
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={option.label}
                            avatar={<option.icon className="!ml-2 !w-5" />}
                        />
                    ))
                }
            />
        </>
    );
};

export { SetupStep };
